import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest, parseErrorMessage } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { MoreHorizontal, Wrench, CalendarClock, UserCog, Hammer, StickyNote, Link2, Unlink, Flag, Edit, Trash2, Loader2, UserCheck, Clock, MessageCircle, Plus, ChevronDown, ChevronUp } from "lucide-react";
import { HouseChatMaintenanceCard } from "@/components/house-chat-maintenance-card";
import { MentionNoteInput, renderNoteBody } from "../app-messages/ChatBubble";
import type { TeamMember } from "@shared/schema";

// Escape regex metacharacters in a user-supplied mention token (e.g. a teammate
// first name containing "(") so building the mention RegExp can't throw at save.
const escapeRegExp = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

// Local time formatter matching the inbox note style (e.g. "3:42 PM").
function noteTime(iso: string): string {
  const d = new Date(iso);
  return isNaN(d.getTime()) ? "" : d.toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}
type MentionChip = { token: string; userId: number; displayName: string };

// The host-enriched house-chat message shape returned by
// GET /api/app/properties/:id/house-chat (forHost: true).
export interface HouseChatHostTask {
  id: number; title: string; status: string;
  priority: string | null; dueDate: string | null;
  assigneeId: number | null; assigneeName: string | null;
  vendorId: number | null; vendorName: string | null;
}
export interface HouseChatHostMessageData {
  id: number;
  body: string;
  senderName: string;
  senderType?: string;
  mine: boolean;
  createdAt: string;
  kind?: string;
  task?: HouseChatHostTask | null;
  linkedTaskId?: number | null;
  assigneeId?: number | null;
  assigneeName?: string | null;
  waitingReplyAt?: string | null;
  noteCount?: number;
  lastNoteAuthor?: string | null;
  lastNoteAt?: string | null;
}

interface TeamMemberRow { id: number; userId?: number | null; name?: string | null; email?: string | null; role: string; status?: string; inviteeUserId?: number | null }
interface VendorRow { id: number; name: string }
interface Note { id: number; body: string; authorName: string; authorUserId: number; mentions: number[]; createdAt: string; updatedAt: string }

const STATUS_OPTIONS: { value: string; label: string }[] = [
  { value: "open", label: "Open" },
  { value: "in_progress", label: "In progress" },
  { value: "on_hold", label: "On hold" },
  { value: "completed", label: "Completed" },
];
const PRIORITY_OPTIONS: { value: string; label: string }[] = [
  { value: "urgent", label: "Urgent" },
  { value: "high", label: "High" },
  { value: "medium", label: "Medium" },
  { value: "low", label: "Low" },
  { value: "batch", label: "Batch" },
];

type ActionDialog = "push" | "link" | "due" | "assignee" | "vendor" | "status" | "priority" | "teamAssign" | null;

// A single host-side house-chat message: the bubble (or maintenance card for a
// member-reported request), plus the triage affordances the inbox has —
// three-dot menu to push/link a ticket, set due date, assign a teammate/vendor,
// set status/priority — and host-only internal notes underneath. There is no
// "resolve" action: house-chat messages are treated as resolved by default.
export function HouseChatHostMessage({
  message,
  propertyId,
  onChanged,
}: {
  message: HouseChatHostMessageData;
  propertyId: number;
  onChanged: () => void;
}) {
  const { toast } = useToast();
  const [action, setAction] = useState<ActionDialog>(null);
  const [notesOpen, setNotesOpen] = useState(false);

  const linked = message.linkedTaskId != null;
  const task = message.task ?? null;

  const fail = (verb: string) => (err: unknown) =>
    toast({ title: `Could not ${verb}`, description: parseErrorMessage(err, "Please try again."), variant: "destructive" });
  const done = () => { onChanged(); setAction(null); };

  // ── Rosters (only fetched when a picker dialog opens) ──────────────────────
  const { data: teamMembers = [] } = useQuery<TeamMemberRow[]>({
    queryKey: ["/api/app/team-members"],
    enabled: action === "assignee" || action === "teamAssign",
  });
  const { data: vendors = [] } = useQuery<VendorRow[]>({
    queryKey: ["/api/app/operations/vendors", propertyId],
    queryFn: async () => {
      const res = await fetch(`/api/app/operations/vendors?propertyId=${propertyId}`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to load vendors");
      return res.json();
    },
    enabled: action === "vendor" || action === "push",
  });
  const assignable = teamMembers.filter((m) => m.id === 0 || (m.status === "accepted" && m.inviteeUserId != null));

  // ── Ticket mutations ───────────────────────────────────────────────────────
  const pushMutation = useMutation({
    mutationFn: async (vars: { title: string; description: string; vendorId: number | null }) =>
      apiRequest("POST", `/api/app/house-chat-messages/${message.id}/push-to-maintenance`, vars),
    onSuccess: () => { toast({ title: "Ticket created", description: "This message is now linked to a maintenance ticket." }); done(); },
    onError: fail("create ticket"),
  });
  const unlinkMutation = useMutation({
    mutationFn: async () => apiRequest("DELETE", `/api/app/house-chat-messages/${message.id}/link-to-maintenance`, undefined),
    onSuccess: done,
    onError: fail("unlink ticket"),
  });
  // Message-level workflow (assign teammate + awaiting-reply) — inbox parity.
  const workflowMutation = useMutation({
    mutationFn: async (patch: Record<string, unknown>) =>
      apiRequest("PATCH", `/api/app/house-chat-messages/${message.id}/workflow`, patch),
    onSuccess: done,
    onError: fail("update message"),
  });

  // The three-dot actions menu — rendered adjacent to the bubble (left of an
  // inbound message, right of an outbound one) so it hugs the message like the
  // inbox, instead of sitting at the far edge.
  const actionsMenu = (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          className="mt-1 shrink-0 rounded p-1 text-gray-400 opacity-0 transition hover:bg-gray-100 hover:text-gray-600 focus:opacity-100 group-hover/msg:opacity-100"
          aria-label="Message actions"
          data-testid={`house-chat-message-menu-${message.id}`}
        >
          <MoreHorizontal className="h-4 w-4" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align={message.mine ? "end" : "start"} className="w-56">
        <DropdownMenuItem onClick={() => setNotesOpen((v) => !v)} data-testid={`house-chat-add-note-${message.id}`}>
          <StickyNote className="mr-2 h-4 w-4" /> {message.noteCount ? "View internal notes" : "Add internal note"}
        </DropdownMenuItem>
        {/* Message-level workflow — inbox parity, on outbound (host) messages */}
        {message.mine && (
          <>
            <DropdownMenuItem onClick={() => setAction("teamAssign")} data-testid={`house-chat-assign-teammate-${message.id}`}>
              <UserCheck className="mr-2 h-4 w-4" /> {message.assigneeName ? "Reassign team member" : "Assign to team member"}
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => workflowMutation.mutate({ waitingReplyAt: message.waitingReplyAt ? null : new Date().toISOString() })}
              data-testid={`house-chat-awaiting-${message.id}`}
            >
              <Clock className="mr-2 h-4 w-4" /> {message.waitingReplyAt ? "Clear awaiting reply" : "Mark awaiting reply"}
            </DropdownMenuItem>
          </>
        )}
        <DropdownMenuSeparator />
        {!linked ? (
          <>
            <DropdownMenuItem onClick={() => setAction("push")} data-testid={`house-chat-push-ticket-${message.id}`}>
              <Wrench className="mr-2 h-4 w-4" /> Create maintenance ticket
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => setAction("link")}>
              <Link2 className="mr-2 h-4 w-4" /> Link to existing ticket
            </DropdownMenuItem>
          </>
        ) : (
          <>
            <DropdownMenuLabel className="text-[11px] font-normal text-gray-400">Ticket #{task?.id}</DropdownMenuLabel>
            <DropdownMenuItem onClick={() => setAction("due")}><CalendarClock className="mr-2 h-4 w-4" /> Set due date</DropdownMenuItem>
            <DropdownMenuItem onClick={() => setAction("assignee")}><UserCog className="mr-2 h-4 w-4" /> Assign ticket to teammate</DropdownMenuItem>
            <DropdownMenuItem onClick={() => setAction("vendor")}><Hammer className="mr-2 h-4 w-4" /> Assign vendor</DropdownMenuItem>
            <DropdownMenuItem onClick={() => setAction("status")}><Flag className="mr-2 h-4 w-4" /> Set status</DropdownMenuItem>
            <DropdownMenuItem onClick={() => setAction("priority")}><Flag className="mr-2 h-4 w-4" /> Set priority</DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => unlinkMutation.mutate()} className="text-red-600 focus:text-red-600">
              <Unlink className="mr-2 h-4 w-4" /> Unlink ticket
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );

  return (
    <div className={`group/msg mb-1 flex w-full flex-col ${message.mine ? "items-end" : "items-start"}`} data-testid={`house-chat-host-message-${message.id}`}>
      {/* Bubble row — three-dot sits right beside the message */}
      <div className="flex max-w-[92%] items-start gap-1">
        {!message.mine && actionsMenu}
        <div className={`flex min-w-0 flex-col ${message.mine ? "items-end" : "items-start"}`}>
          {message.kind === "maintenance" ? (
            <div className="w-full">
              <HouseChatMaintenanceCard
                title={task?.title ?? message.body.replace(/^🔧\s*Maintenance request:\s*/u, "")}
                senderName={message.senderName}
                task={task}
              />
            </div>
          ) : (
            <>
              {!message.mine && <span className="mb-0.5 px-1 text-xs text-gray-500">{message.senderName}</span>}
              <div className={`rounded-2xl px-3 py-2 text-sm ${message.mine ? "bg-brand text-brand-foreground" : "border bg-white"}`}>
                {message.body}
              </div>
              <span className="mt-0.5 px-1 text-[10px] text-gray-400">
                {new Date(message.createdAt).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
              </span>
            </>
          )}

          {/* Ticket / triage chips (host-only, on any linked message) */}
          {linked && task && (
            <div className={`mt-1 flex flex-wrap gap-1 ${message.mine ? "justify-end" : "justify-start"}`}>
              <a
                href={`/app/tasks?task=${task.id}`}
                className="inline-flex items-center gap-1 rounded-full border border-green-300 bg-green-50 px-2 py-0.5 text-[11px] font-medium text-green-800 hover:bg-green-100"
                data-testid={`house-chat-ticket-badge-${message.id}`}
              >
                <Wrench className="h-3 w-3" /> Ticket #{task.id}
              </a>
              {task.dueDate && (
                <span className="inline-flex items-center gap-1 rounded-full border bg-gray-50 px-2 py-0.5 text-[11px] text-gray-600">
                  <CalendarClock className="h-3 w-3" /> {new Date(task.dueDate).toLocaleDateString([], { month: "short", day: "numeric" })}
                </span>
              )}
              {task.assigneeName && (
                <span className="inline-flex items-center gap-1 rounded-full border bg-gray-50 px-2 py-0.5 text-[11px] text-gray-600">
                  <UserCog className="h-3 w-3" /> {task.assigneeName}
                </span>
              )}
              {task.vendorName && (
                <span className="inline-flex items-center gap-1 rounded-full border bg-gray-50 px-2 py-0.5 text-[11px] text-gray-600">
                  <Hammer className="h-3 w-3" /> {task.vendorName}
                </span>
              )}
            </div>
          )}

          {/* Message-level workflow pills (assignee + awaiting reply) */}
          {(message.assigneeName || message.waitingReplyAt) && (
            <div className={`mt-1 flex flex-wrap gap-1 ${message.mine ? "justify-end" : "justify-start"}`}>
              {message.assigneeName && (
                <span className="inline-flex items-center gap-1 rounded-full border border-indigo-200 bg-indigo-50 px-2 py-0.5 text-[11px] font-medium text-indigo-700">
                  <UserCheck className="h-3 w-3" /> {message.assigneeName}
                </span>
              )}
              {message.waitingReplyAt && (
                <span className="inline-flex items-center gap-1 rounded-full border border-amber-300 bg-amber-100 px-2 py-0.5 text-[11px] font-medium text-amber-800">
                  <Clock className="h-3 w-3" /> Awaiting reply
                </span>
              )}
            </div>
          )}
        </div>
        {message.mine && actionsMenu}
      </div>

      {/* Visible note affordance (inbox-style): add-note or "N notes · last by X" */}
      <button
        onClick={() => setNotesOpen((v) => !v)}
        className={`mt-0.5 flex items-center gap-1 px-1 text-[11px] ${message.noteCount ? "text-blue-500 hover:text-blue-700" : "text-gray-400 hover:text-gray-600"}`}
        data-testid={`house-chat-note-toggle-${message.id}`}
      >
        {message.noteCount ? (
          <>
            <MessageCircle className="h-3 w-3" />
            {message.noteCount} {message.noteCount === 1 ? "note" : "notes"}
            {message.lastNoteAuthor && (
              <span className="text-gray-400"> · last by {message.lastNoteAuthor}{message.lastNoteAt ? `, ${noteTime(message.lastNoteAt)}` : ""}</span>
            )}
            {notesOpen ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
          </>
        ) : notesOpen ? (
          <><ChevronUp className="h-3 w-3" /> Hide notes</>
        ) : (
          <><Plus className="h-2.5 w-2.5" /> Add internal note</>
        )}
      </button>

      {/* Internal notes */}
      {notesOpen && (
        <div className="mt-1 w-full max-w-lg">
          <NotesPanel messageId={message.id} onChanged={onChanged} />
        </div>
      )}

      {/* Action dialogs */}
      {action === "push" && (
        <PushTicketDialog
          defaultTitle={message.body.replace(/^🔧\s*Maintenance request:\s*/u, "").slice(0, 200)}
          vendors={vendors}
          isPending={pushMutation.isPending}
          onCancel={() => setAction(null)}
          onSubmit={(vars) => pushMutation.mutate(vars)}
        />
      )}
      {action === "link" && (
        <LinkTicketDialog messageId={message.id} propertyId={propertyId} onCancel={() => setAction(null)} onLinked={done} onError={fail("link ticket")} />
      )}
      {action === "due" && task && (
        <DueDateDialog current={task.dueDate} onCancel={() => setAction(null)} taskId={task.id} onDone={done} onError={fail("set due date")} />
      )}
      {action === "assignee" && task && (
        <SelectDialog
          title="Assign teammate"
          current={task.assigneeName}
          placeholder="Select a teammate"
          options={[{ value: "null", label: "Unassigned" }, ...assignable.map((m) => ({ value: String(m.id), label: m.role === "owner" ? "Me (owner)" : (m.name || m.email || `Teammate ${m.id}`) }))]}
          taskId={task.id}
          toPatch={(v) => ({ assigneeId: v === "null" ? null : Number(v) })}
          onCancel={() => setAction(null)}
          onDone={done}
          onError={fail("assign teammate")}
        />
      )}
      {action === "teamAssign" && (
        <MessageAssignDialog
          assignable={assignable}
          current={message.assigneeName}
          isPending={workflowMutation.isPending}
          onCancel={() => setAction(null)}
          onSave={(assigneeId, assigneeName) => workflowMutation.mutate({ assigneeId, assigneeName })}
        />
      )}
      {action === "vendor" && task && (
        <SelectDialog
          title="Assign vendor"
          current={task.vendorName}
          placeholder="Select a vendor"
          options={[{ value: "null", label: "No vendor" }, ...vendors.map((v) => ({ value: String(v.id), label: v.name }))]}
          taskId={task.id}
          toPatch={(v) => ({ vendorId: v === "null" ? null : Number(v) })}
          onCancel={() => setAction(null)}
          onDone={done}
          onError={fail("assign vendor")}
        />
      )}
      {action === "status" && task && (
        <SelectDialog
          title="Set status"
          current={STATUS_OPTIONS.find((s) => s.value === task.status)?.label ?? task.status}
          placeholder="Select a status"
          options={STATUS_OPTIONS}
          taskId={task.id}
          toPatch={(v) => ({ status: v })}
          onCancel={() => setAction(null)}
          onDone={done}
          onError={fail("set status")}
        />
      )}
      {action === "priority" && task && (
        <SelectDialog
          title="Set priority"
          current={PRIORITY_OPTIONS.find((p) => p.value === task.priority)?.label ?? task.priority}
          placeholder="Select a priority"
          options={PRIORITY_OPTIONS}
          taskId={task.id}
          toPatch={(v) => ({ priority: v })}
          onCancel={() => setAction(null)}
          onDone={done}
          onError={fail("set priority")}
        />
      )}
    </div>
  );
}

// ─── Push-to-ticket dialog ─────────────────────────────────────────────────────
function PushTicketDialog({
  defaultTitle, vendors, isPending, onCancel, onSubmit,
}: {
  defaultTitle: string;
  vendors: VendorRow[];
  isPending: boolean;
  onCancel: () => void;
  onSubmit: (vars: { title: string; description: string; vendorId: number | null }) => void;
}) {
  const [title, setTitle] = useState(defaultTitle);
  const [description, setDescription] = useState("");
  const [vendorId, setVendorId] = useState<string>("null");
  return (
    <Dialog open onOpenChange={(o) => { if (!o && !isPending) onCancel(); }}>
      <DialogContent onInteractOutside={(e) => { if (isPending) e.preventDefault(); }} onEscapeKeyDown={(e) => { if (isPending) e.preventDefault(); }}>
        <DialogHeader>
          <DialogTitle>Create maintenance ticket</DialogTitle>
          <DialogDescription>Turn this house-chat message into a maintenance ticket on your task board.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label htmlFor="ht-title">Title</Label>
            <Input id="ht-title" value={title} onChange={(e) => setTitle(e.target.value)} />
          </div>
          <div>
            <Label htmlFor="ht-desc">Details (optional)</Label>
            <Textarea id="ht-desc" rows={3} value={description} onChange={(e) => setDescription(e.target.value)} />
          </div>
          <div>
            <Label>Vendor (optional)</Label>
            <Select value={vendorId} onValueChange={setVendorId}>
              <SelectTrigger><SelectValue placeholder="No vendor" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="null">No vendor</SelectItem>
                {vendors.map((v) => <SelectItem key={v.id} value={String(v.id)}>{v.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onCancel} disabled={isPending}>Cancel</Button>
          <Button
            className="bg-brand hover:bg-brand-hover"
            disabled={isPending || !title.trim()}
            onClick={() => onSubmit({ title: title.trim(), description: description.trim(), vendorId: vendorId === "null" ? null : Number(vendorId) })}
          >
            {isPending ? "Creating…" : "Create ticket"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Link-to-existing-ticket dialog ────────────────────────────────────────────
function LinkTicketDialog({
  messageId, propertyId, onCancel, onLinked, onError,
}: {
  messageId: number;
  propertyId: number;
  onCancel: () => void;
  onLinked: () => void;
  onError: (err: unknown) => void;
}) {
  const [taskId, setTaskId] = useState<string>("");
  const { data: tasks = [] } = useQuery<{ id: number; title: string; status: string; propertyId: number | null }[]>({
    queryKey: ["/api/app/tasks"],
  });
  const open = tasks
    .filter((t) => t.status !== "completed" && t.status !== "complete" && t.status !== "paid")
    .sort((a, b) => (a.propertyId === propertyId ? -1 : 1) - (b.propertyId === propertyId ? -1 : 1));
  const link = useMutation({
    mutationFn: async () => apiRequest("POST", `/api/app/house-chat-messages/${messageId}/link-to-maintenance/${taskId}`, undefined),
    onSuccess: onLinked,
    onError,
  });
  return (
    <Dialog open onOpenChange={(o) => { if (!o && !link.isPending) onCancel(); }}>
      <DialogContent onInteractOutside={(e) => { if (link.isPending) e.preventDefault(); }} onEscapeKeyDown={(e) => { if (link.isPending) e.preventDefault(); }}>
        <DialogHeader>
          <DialogTitle>Link to existing ticket</DialogTitle>
          <DialogDescription>Attach this message to a maintenance ticket that's already on your board.</DialogDescription>
        </DialogHeader>
        <Select value={taskId} onValueChange={setTaskId}>
          <SelectTrigger><SelectValue placeholder="Select a ticket" /></SelectTrigger>
          <SelectContent>
            {open.map((t) => <SelectItem key={t.id} value={String(t.id)}>{t.title} (#{t.id})</SelectItem>)}
          </SelectContent>
        </Select>
        <DialogFooter>
          <Button variant="outline" onClick={onCancel} disabled={link.isPending}>Cancel</Button>
          <Button className="bg-brand hover:bg-brand-hover" disabled={link.isPending || !taskId} onClick={() => link.mutate()}>
            {link.isPending ? "Linking…" : "Link ticket"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Due-date dialog ────────────────────────────────────────────────────────────
function DueDateDialog({
  current, taskId, onCancel, onDone, onError,
}: {
  current: string | null;
  taskId: number;
  onCancel: () => void;
  onDone: () => void;
  onError: (err: unknown) => void;
}) {
  // datetime-local wants "YYYY-MM-DDTHH:mm"; seed from the current due date.
  const seed = current ? new Date(current) : null;
  const [value, setValue] = useState(seed && !isNaN(seed.getTime())
    ? new Date(seed.getTime() - seed.getTimezoneOffset() * 60000).toISOString().slice(0, 16)
    : "");
  const save = useMutation({
    mutationFn: async (dueDate: string | null) => apiRequest("PATCH", `/api/app/operations/tasks/${taskId}`, { dueDate }),
    onSuccess: onDone,
    onError,
  });
  return (
    <Dialog open onOpenChange={(o) => { if (!o && !save.isPending) onCancel(); }}>
      <DialogContent onInteractOutside={(e) => { if (save.isPending) e.preventDefault(); }} onEscapeKeyDown={(e) => { if (save.isPending) e.preventDefault(); }}>
        <DialogHeader>
          <DialogTitle>Set due date</DialogTitle>
          <DialogDescription>Set an SLA / due date for this ticket.</DialogDescription>
        </DialogHeader>
        <Input type="datetime-local" value={value} onChange={(e) => setValue(e.target.value)} />
        <DialogFooter className="flex-col-reverse gap-2 sm:flex-row">
          {current && (
            <Button variant="ghost" className="text-red-600" onClick={() => save.mutate(null)} disabled={save.isPending}>Clear</Button>
          )}
          <Button variant="outline" onClick={onCancel} disabled={save.isPending}>Cancel</Button>
          <Button
            className="bg-brand hover:bg-brand-hover"
            disabled={save.isPending || !value}
            onClick={() => save.mutate(new Date(value).toISOString())}
          >
            {save.isPending ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Generic single-select dialog (assignee / vendor / status / priority) ───────
function SelectDialog({
  title, current, placeholder, options, taskId, toPatch, onCancel, onDone, onError,
}: {
  title: string;
  current: string | null | undefined;
  placeholder: string;
  options: { value: string; label: string }[];
  taskId: number;
  toPatch: (value: string) => Record<string, unknown>;
  onCancel: () => void;
  onDone: () => void;
  onError: (err: unknown) => void;
}) {
  const [value, setValue] = useState<string>("");
  const save = useMutation({
    mutationFn: async () => apiRequest("PATCH", `/api/app/operations/tasks/${taskId}`, toPatch(value)),
    onSuccess: onDone,
    onError,
  });
  return (
    <Dialog open onOpenChange={(o) => { if (!o && !save.isPending) onCancel(); }}>
      <DialogContent onInteractOutside={(e) => { if (save.isPending) e.preventDefault(); }} onEscapeKeyDown={(e) => { if (save.isPending) e.preventDefault(); }}>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {current && <DialogDescription>Currently: {current}</DialogDescription>}
        </DialogHeader>
        <Select value={value} onValueChange={setValue}>
          <SelectTrigger><SelectValue placeholder={placeholder} /></SelectTrigger>
          <SelectContent>
            {options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <DialogFooter>
          <Button variant="outline" onClick={onCancel} disabled={save.isPending}>Cancel</Button>
          <Button className="bg-brand hover:bg-brand-hover" disabled={save.isPending || !value} onClick={() => save.mutate()}>
            {save.isPending ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Message-level "assign to team member" (inbox parity, outbound messages) ────
// Stores an app-user id + name on the house-chat message (not a task). The owner
// row uses its userId; teammates use inviteeUserId — both validated server-side.
function MessageAssignDialog({
  assignable, current, isPending, onCancel, onSave,
}: {
  assignable: TeamMemberRow[];
  current: string | null | undefined;
  isPending: boolean;
  onCancel: () => void;
  onSave: (assigneeId: number | null, assigneeName: string | null) => void;
}) {
  const [value, setValue] = useState<string>("");
  // value encodes the target app-user id, or "null" to unassign.
  const options = assignable
    .map((m) => {
      const appUserId = m.role === "owner" ? m.userId : m.inviteeUserId;
      if (appUserId == null) return null;
      const label = m.role === "owner" ? "Me (owner)" : (m.name || m.email || `Teammate ${m.id}`);
      return { value: String(appUserId), label };
    })
    .filter((o): o is { value: string; label: string } => o != null);
  const nameForValue = (v: string) => options.find((o) => o.value === v)?.label ?? null;

  return (
    <Dialog open onOpenChange={(o) => { if (!o && !isPending) onCancel(); }}>
      <DialogContent onInteractOutside={(e) => { if (isPending) e.preventDefault(); }} onEscapeKeyDown={(e) => { if (isPending) e.preventDefault(); }}>
        <DialogHeader>
          <DialogTitle>Assign to team member</DialogTitle>
          {current && <DialogDescription>Currently: {current}</DialogDescription>}
        </DialogHeader>
        <Select value={value} onValueChange={setValue}>
          <SelectTrigger><SelectValue placeholder="Select a teammate" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="null">Unassigned</SelectItem>
            {options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <DialogFooter>
          <Button variant="outline" onClick={onCancel} disabled={isPending}>Cancel</Button>
          <Button
            className="bg-brand hover:bg-brand-hover"
            disabled={isPending || !value}
            onClick={() => value === "null" ? onSave(null, null) : onSave(Number(value), nameForValue(value))}
          >
            {isPending ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Internal notes panel ───────────────────────────────────────────────────────
// Chips row shared by the composer + note editor (mirrors the inbox thread UI).
function MentionChips({ chips, onRemove }: { chips: MentionChip[]; onRemove: (userId: number) => void }) {
  if (chips.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-1">
      {chips.map((chip) => (
        <span key={chip.userId} className="inline-flex items-center gap-1 rounded-full bg-purple-100 px-2 py-0.5 text-[10px] font-medium text-purple-700">
          @{chip.displayName}
          <button type="button" onClick={() => onRemove(chip.userId)} className="ml-0.5 text-purple-400 hover:text-purple-700" aria-label={`Remove mention of ${chip.displayName}`}>×</button>
        </span>
      ))}
    </div>
  );
}

// One internal note — matches the inbox ThreadNote: author + time, an
// author-only three-dot (Edit / Delete), @mention chips in the editor, and
// purple-highlighted mention tokens when rendered.
function NoteRow({
  note, messageId, teamMembers, currentUserId, onChanged, notesKey,
}: {
  note: Note; messageId: number; teamMembers: TeamMember[]; currentUserId: number | null;
  onChanged: () => void; notesKey: (string | number)[];
}) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [editBody, setEditBody] = useState(note.body);
  const [editChips, setEditChips] = useState<MentionChip[]>([]);
  const isAuthor = currentUserId != null && note.authorUserId === currentUserId;
  const invalidate = () => { queryClient.invalidateQueries({ queryKey: notesKey }); onChanged(); };

  function startEdit() {
    setEditBody(note.body);
    const chips: MentionChip[] = [];
    for (const uid of note.mentions ?? []) {
      const tm = teamMembers.find((m) => m.inviteeUserId === uid || (m.id === 0 && m.userId === uid));
      if (tm) {
        const fullName = tm.name ?? tm.email.split("@")[0];
        chips.push({ token: fullName.split(/\s+/)[0], userId: uid, displayName: fullName });
      }
    }
    setEditChips(chips);
    setEditing(true);
  }

  const edit = useMutation({
    mutationFn: async () => {
      const present = editChips.filter((c) => new RegExp(`@${escapeRegExp(c.token)}(?:[\\s,.]|$)`).test(editBody));
      const mentions = present.map((c) => c.userId);
      return apiRequest("PATCH", `/api/app/house-chat-messages/${messageId}/notes/${note.id}`, { body: editBody.trim(), ...(mentions.length ? { mentions } : {}) });
    },
    onSuccess: () => { setEditing(false); invalidate(); },
    onError: (err) => toast({ title: "Could not save edit", description: parseErrorMessage(err, "Please try again."), variant: "destructive" }),
  });
  const del = useMutation({
    mutationFn: async () => apiRequest("DELETE", `/api/app/house-chat-messages/${messageId}/notes/${note.id}`, undefined),
    onSuccess: invalidate,
    onError: (err) => toast({ title: "Could not delete note", description: parseErrorMessage(err, "Please try again."), variant: "destructive" }),
  });

  return (
    <li className="group/note rounded border border-amber-200 bg-white p-1.5 text-xs">
      <div className="flex items-start justify-between gap-1">
        <div className="font-medium text-gray-700">
          {note.authorName} <span className="font-normal text-gray-400">· {noteTime(note.createdAt)}</span>
          {note.updatedAt && note.updatedAt !== note.createdAt && <span className="ml-1 font-normal italic text-gray-400">· edited</span>}
        </div>
        {isAuthor && !editing && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button type="button" className="rounded p-0.5 text-gray-400 opacity-0 transition hover:text-gray-600 focus:opacity-100 group-hover/note:opacity-100" aria-label="Note options" data-testid={`house-chat-note-menu-${note.id}`}>
                <MoreHorizontal className="h-3 w-3" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="text-xs">
              <DropdownMenuItem onClick={startEdit}><Edit className="mr-1.5 h-3 w-3" /> Edit</DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => del.mutate()} className="text-red-600 focus:text-red-600"><Trash2 className="mr-1.5 h-3 w-3" /> Delete</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
      {editing ? (
        <div className="mt-1 space-y-1">
          <MentionNoteInput
            value={editBody}
            onChange={setEditBody}
            onMention={(token, userId, displayName) => setEditChips((prev) => prev.some((c) => c.userId === userId) ? prev : [...prev, { token, userId, displayName }])}
            teamMembers={teamMembers}
            chatId={messageId}
            onSubmit={() => { if (editBody.trim() && !edit.isPending) edit.mutate(); }}
          />
          <MentionChips chips={editChips} onRemove={(uid) => setEditChips((prev) => prev.filter((c) => c.userId !== uid))} />
          <div className="flex justify-end gap-1">
            <Button type="button" variant="ghost" size="sm" className="h-6 px-2 text-xs" onClick={() => setEditing(false)} disabled={edit.isPending}>Cancel</Button>
            <Button type="button" size="sm" className="h-6 bg-brand px-2 text-xs hover:bg-brand-hover" disabled={!editBody.trim() || edit.isPending} onClick={() => edit.mutate()}>
              {edit.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : "Save"}
            </Button>
          </div>
        </div>
      ) : (
        <div className="mt-0.5 whitespace-pre-wrap break-words text-gray-800">
          {renderNoteBody(note.body, note.mentions, teamMembers)}
        </div>
      )}
    </li>
  );
}

function NotesPanel({ messageId, onChanged }: { messageId: number; onChanged: () => void }) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState("");
  const [chips, setChips] = useState<MentionChip[]>([]);
  const notesKey = ["/api/app/house-chat-messages", messageId, "notes"];

  const { data: notes = [] } = useQuery<Note[]>({
    queryKey: notesKey,
    queryFn: async () => {
      const res = await fetch(`/api/app/house-chat-messages/${messageId}/notes`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to load notes");
      return res.json();
    },
  });
  const { data: teamMembers = [] } = useQuery<TeamMember[]>({ queryKey: ["/api/app/team-members"] });
  const { data: me } = useQuery<{ user?: { id: number } }>({ queryKey: ["/api/app/auth/me"] });
  const currentUserId = me?.user?.id ?? null;

  const invalidate = () => { queryClient.invalidateQueries({ queryKey: notesKey }); onChanged(); };

  const add = useMutation({
    mutationFn: async () => {
      const present = chips.filter((c) => new RegExp(`@${escapeRegExp(c.token)}(?:[\\s,.]|$)`).test(draft));
      const mentions = present.map((c) => c.userId);
      return apiRequest("POST", `/api/app/house-chat-messages/${messageId}/notes`, { body: draft.trim(), ...(mentions.length ? { mentions } : {}) });
    },
    onSuccess: () => { setDraft(""); setChips([]); invalidate(); },
    onError: (err) => toast({ title: "Could not add note", description: parseErrorMessage(err, "Please try again."), variant: "destructive" }),
  });

  return (
    <div className="rounded-lg border border-amber-200 bg-amber-50/60 p-2" data-testid={`house-chat-notes-${messageId}`}>
      <p className="mb-1.5 flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wide text-amber-700">
        <StickyNote className="h-3 w-3" /> Internal notes — only your team sees these
      </p>
      <ul className="space-y-1.5">
        {notes.map((n) => (
          <NoteRow key={n.id} note={n} messageId={messageId} teamMembers={teamMembers} currentUserId={currentUserId} onChanged={onChanged} notesKey={notesKey} />
        ))}
      </ul>
      <div className="mt-1.5 space-y-1">
        <div className="flex items-start gap-1">
          <MentionNoteInput
            value={draft}
            onChange={setDraft}
            onMention={(token, userId, displayName) => setChips((prev) => prev.some((c) => c.userId === userId) ? prev : [...prev, { token, userId, displayName }])}
            teamMembers={teamMembers}
            chatId={messageId}
            onSubmit={() => { if (draft.trim() && !add.isPending) add.mutate(); }}
          />
          <Button type="button" size="sm" className="h-7 shrink-0 bg-brand px-3 text-xs hover:bg-brand-hover" disabled={add.isPending || !draft.trim()} onClick={() => add.mutate()}>
            {add.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : "Add"}
          </Button>
        </div>
        <MentionChips chips={chips} onRemove={(uid) => setChips((prev) => prev.filter((c) => c.userId !== uid))} />
      </div>
    </div>
  );
}
